export const HANDOFF_DOMAINS = [
  { id: 'auvik_inventory', label: 'Auvik devices and interfaces' },
  { id: 'auvik_alerts', label: 'Auvik alerts' },
  { id: 'unifi_inventory', label: 'UniFi sites and access points' },
  { id: 'unifi_wlan', label: 'UniFi wireless networks and clients' },
] as const;

export type HandoffDomain = typeof HANDOFF_DOMAINS[number]['id'];
export type HandoffOwner = 'portal' | 'mission_control';

function object(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

/** Summary values are deliberately limited to counts; source record text is never rendered. */
export function handoffSummary(domain: HandoffDomain, payload: unknown) {
  const row = object(payload);
  const fields = {
    auvik_inventory: [['devices', 'Devices'], ['interfaces', 'Interfaces']],
    auvik_alerts: [['alerts', 'Alerts']],
    unifi_inventory: [['sites', 'Sites'], ['access_points', 'Access points']],
    unifi_wlan: [['wlans', 'Wireless networks'], ['clients', 'Clients']],
  }[domain];
  return fields.map(([key, label]) => ({
    label,
    count: Array.isArray(row[key]) && row[key].length <= 10_000 ? row[key].length : null,
  }));
}

export function handoffSnapshotState(stage: {
  link_version: number; observed_at: string; payload: unknown;
} | undefined, linkVersion: number, now = Date.now()) {
  if (!stage) return { ready: false, label: 'Awaiting a source snapshot' };
  if (stage.link_version !== linkVersion) return { ready: false, label: 'Snapshot belongs to an earlier network review' };
  if (object(stage.payload).complete !== true) return { ready: false, label: 'Source snapshot is incomplete' };
  const observed = Date.parse(stage.observed_at);
  if (!Number.isFinite(observed) || observed > now + 60_000) return { ready: false, label: 'Source observation time is invalid' };
  if (now - observed > 10 * 60_000) return { ready: false, label: 'Source snapshot is older than 10 minutes' };
  return { ready: true, label: 'Recent complete source snapshot' };
}

export function handoffError(error: unknown) {
  const code = object(error).code;
  if (code === 'PT409' || code === '40001') return 'The network, snapshot or data owner changed. Reload, compare the current records and review again.';
  if (code === '42501') return 'Your current session cannot change this data owner. Sign in as an administrator and review the current network.';
  if (code === '23505') return 'This provider or UniFi host already has a reviewed data owner. Resolve its existing handoff first.';
  if (code === '22023') return 'The handoff did not qualify. Check the recent complete provider snapshot and complete location-wide uptime coverage, then review again.';
  return 'The data owner could not be changed. Reload and review the current state before trying again.';
}
